/**
 * Lindy Price Filter Component
 * Handles dual range slider for price filtering with debounced updates
 *
 * Features:
 * - Event delegation for AJAX compatibility
 * - Value capture to prevent race conditions
 * - Debounced filter application
 * - Instance management for memory safety
 */

// Instance tracking for cleanup
const instances = new Map();

/**
 * Price Filter Class
 * Manages a single price filter instance
 */
class PriceFilterLindy {
  /**
   * @param {string} containerId - ID of the price-facet-component element
   */
  constructor(containerId) {
    // Reuse existing valid instance
    if (instances.has(containerId)) {
      const existing = instances.get(containerId);
      const isStale = existing.container && !document.contains(existing.container);

      if (isStale) {
        existing.destroy();
      } else {
        return;
      }
    }

    // Get container
    this.container = document.getElementById(containerId);
    if (!this.container) return;

    this.containerId = containerId;
    this.debounceTimer = null;
    this.isProcessingRelease = false;
    this.DEBOUNCE_DELAY = 300;

    // Cache initial element references
    this.cacheElements();

    // Validate required elements
    if (!this.minSlider || !this.maxSlider || !this.minInput || !this.maxInput) {
      return;
    }

    // Initialize
    this.bindEvents();
    this.updateVisual();
    instances.set(containerId, this);
  }

  /**
   * Cache DOM element references
   */
  cacheElements() {
    this.minSlider = this.container.querySelector('[data-slider-min]');
    this.maxSlider = this.container.querySelector('[data-slider-max]');
    this.minValue = this.container.querySelector('[data-price-min]');
    this.maxValue = this.container.querySelector('[data-price-max]');
    this.range = this.container.querySelector('[data-slider-range]');

    // Get hidden inputs
    const minInputId = this.container.dataset.minInputId;
    const maxInputId = this.container.dataset.maxInputId;
    this.minInput = document.getElementById(minInputId) ||
                    this.container.querySelector('input[ref="minInput"]');
    this.maxInput = document.getElementById(maxInputId) ||
                    this.container.querySelector('input[ref="maxInput"]');
  }

  /**
   * Refresh element references (for AJAX updates)
   */
  refreshElements() {
    this.minSlider = this.container.querySelector('[data-slider-min]');
    this.maxSlider = this.container.querySelector('[data-slider-max]');
    this.minValue = this.container.querySelector('[data-price-min]');
    this.maxValue = this.container.querySelector('[data-price-max]');
    this.range = this.container.querySelector('[data-slider-range]');
  }

  /**
   * Bind event listeners using delegation
   */
  bindEvents() {
    // Input handler for drag updates
    this.onInput = (e) => {
      if (!e.target.matches('[data-slider-min], [data-slider-max]')) return;
      this.refreshElements();
      this.updateVisual();
    };

    // Release handler for filter application
    this.onRelease = (e) => {
      if (!e.target.matches('[data-slider-min], [data-slider-max]')) return;

      // Prevent duplicate events (change + pointerup both fire)
      if (this.isProcessingRelease) return;
      this.isProcessingRelease = true;
      setTimeout(() => { this.isProcessingRelease = false; }, 50);

      this.refreshElements();
      if (!this.minSlider || !this.maxSlider) return;

      // Capture values immediately before anything can modify them
      const min = this.minSlider.value;
      const max = this.maxSlider.value;

      this.updateVisual();
      this.scheduleFilter(min, max);
    };

    // Attach to container (event delegation)
    this.container.addEventListener('input', this.onInput);
    this.container.addEventListener('change', this.onRelease);
    this.container.addEventListener('pointerup', this.onRelease);
  }

  /**
   * Update visual display (range bar and labels)
   */
  updateVisual() {
    if (!this.minSlider || !this.maxSlider || !this.range) return;

    const min = Number(this.minSlider.value);
    const max = Number(this.maxSlider.value);
    const sliderMax = Number(this.minSlider.max);

    // Prevent crossing
    if (min >= max) {
      this.minSlider.value = max - 1;
    }

    // Update range bar position
    const minPercent = (this.minSlider.value / sliderMax) * 100;
    const maxPercent = (this.maxSlider.value / sliderMax) * 100;

    this.range.style.left = minPercent + '%';
    this.range.style.width = (maxPercent - minPercent) + '%';

    // Update labels
    if (this.minValue) this.minValue.textContent = this.minSlider.value;
    if (this.maxValue) this.maxValue.textContent = this.maxSlider.value;
  }

  /**
   * Schedule filter application with debounce
   * @param {string} min - Captured min value
   * @param {string} max - Captured max value
   */
  scheduleFilter(min, max) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;

      // Verify container still exists
      if (!document.contains(this.container)) return;

      this.applyFilter(min, max);
    }, this.DEBOUNCE_DELAY);
  }

  /**
   * Apply filter values and trigger update
   * @param {string} min - Min price value
   * @param {string} max - Max price value
   */
  applyFilter(min, max) {
    // Update hidden inputs
    this.minInput.value = min;
    this.maxInput.value = max;

    // Trigger filter update
    if (typeof this.container.updatePriceFilterAndResults === 'function') {
      this.container.updatePriceFilterAndResults();
    } else {
      this.container.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Cleanup instance
   */
  destroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.container) {
      this.container.removeEventListener('input', this.onInput);
      this.container.removeEventListener('change', this.onRelease);
      this.container.removeEventListener('pointerup', this.onRelease);
    }

    instances.delete(this.containerId);
  }
}

/**
 * Initialize all price filters
 * Debounced and throttled for performance
 */
let initTimer = null;
let lastInit = 0;
const INIT_DEBOUNCE = 100;
const INIT_THROTTLE = 800;

function initAllPriceFilters() {
  if (initTimer) clearTimeout(initTimer);

  initTimer = setTimeout(() => {
    initTimer = null;

    // Throttle
    const now = Date.now();
    if (now - lastInit < INIT_THROTTLE) return;
    lastInit = now;

    document.querySelectorAll('price-facet-component[id]').forEach(el => {
      new PriceFilterLindy(el.id);
    });
  }, INIT_DEBOUNCE);
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', initAllPriceFilters);

// Shopify theme editor
document.addEventListener('shopify:section:load', initAllPriceFilters);

// After AJAX filter updates
document.addEventListener('filter:update', () => {
  setTimeout(initAllPriceFilters, 500);
});

// Mobile drawer open
document.addEventListener('click', (e) => {
  const target = e.target;
  if (target?.closest?.('[on\\:click*="filters-drawer"], .lindy-mobile-refine-btn, .facets-toggle__button')) {
    setTimeout(initAllPriceFilters, 100);
  }
});

// Dialog observer for drawer
const setupDialogObserver = () => {
  const drawer = document.getElementById('filters-drawer');
  const dialog = drawer?.querySelector('dialog');
  if (!dialog) return;

  new MutationObserver((mutations) => {
    if (mutations.some(m => m.attributeName === 'open') && dialog.open) {
      setTimeout(initAllPriceFilters, 50);
    }
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupDialogObserver);
} else {
  setupDialogObserver();
}
